import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { fmtMs, fmtBytes, now, clamp } from '@/utils/format';
import { qualityFromTime, scoreFromTime } from '@/utils/scoring';

interface Props { autoRun: boolean; autoNonce: number; }

interface DataRow { id: number; sku: string; region: number; qty: number; price: number; ts: number; }

export function NetworkTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [records, setRecords] = useState(120000);
  const [metrics, setMetrics] = useState({
    stringifyMs: null as number | null, parseMs: null as number | null,
    renderMs: null as number | null, jsonBytes: null as number | null,
  });
  const [preview, setPreview] = useState<DataRow[]>([]);
  const [status, setStatus] = useState('idle');
  const startRef = useRef(0);
  const autoDoneRef = useRef(false);

  function reset() {
    setRunning(false); autoDoneRef.current = false;
    setMetrics({ stringifyMs: null, parseMs: null, renderMs: null, jsonBytes: null });
    setPreview([]); setStatus('idle');
  }

  function makeData(n: number): DataRow[] {
    let seed = 135791113;
    function rnd() { seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5; return (seed >>> 0) / 4294967295; }
    const out = new Array(n);
    const base = Date.now();
    for (let i = 0; i < n; i++) {
      const r = rnd();
      out[i] = { id: i + 1, sku: 'SKU-' + ((r * 1e9) >>> 0).toString(16), region: i % 13, qty: (r * 1000) | 0, price: Math.round(r * 100000) / 100, ts: base - (i % 10000) * 1000 };
    }
    return out;
  }

  async function start() {
    reset();
    autoDoneRef.current = false;
    setRunning(true);
    setStatus('generating');
    const n = clamp(parseInt(String(records), 10) || 120000, 20000, 250000);
    setRecords(n);
    await new Promise((r) => setTimeout(r, 0));

    const data = makeData(n);
    setStatus('stringify');
    const t0 = now();
    const json = JSON.stringify(data);
    const sMs = now() - t0;

    setStatus('parse');
    const t1 = now();
    const parsed = JSON.parse(json) as DataRow[];
    const pMs = now() - t1;
    const bytes = json.length * 2;

    setStatus('render');
    startRef.current = now();
    setPreview(parsed.slice(0, 220));

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const rMs = now() - startRef.current;
        setMetrics({ stringifyMs: sMs, parseMs: pMs, renderMs: rMs, jsonBytes: bytes });
        setStatus('done');
        setRunning(false);

        const sc = Math.round(0.45 * scoreFromTime(pMs, 400, 900) + 0.35 * scoreFromTime(rMs, 120, 260) + 0.2 * scoreFromTime(sMs, 500, 1200));
        const testId: TestId = 'tv-network';
        reportResult(testId, { score: sc, primary: `Parse ${fmtMs(pMs)} · JSON ${fmtBytes(bytes)}`, details: { stringifyMs: sMs, parseMs: pMs, renderMs: rMs, jsonBytes: bytes } });
        if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
      }),
    );
  }

  useEffect(() => {
    if (!autoRun) return;
    setRecords(120000);
    start();
  }, [autoRun, autoNonce]);

  const qParse = qualityFromTime(metrics.parseMs, 400, 900);

  return (
    <TestCard title={t('tv.network.title', language)} description={t('tv.network.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
        <div className="flex-1" />
        <div className="field">
          <div className="label">{t('label.records', language)}</div>
          <input value={records} disabled={running || autoRun} onChange={(e) => setRecords(Number(e.target.value))} inputMode="numeric" className="input w-28" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('metric.stringify', language)} value={fmtMs(metrics.stringifyMs)} />
        <MetricBox label={t('metric.parse', language)} value={fmtMs(metrics.parseMs)} hint={t('hint.parseGood', language)} />
        <MetricBox label={t('metric.renderSlice', language)} value={fmtMs(metrics.renderMs)} />
        <MetricBox label={t('metric.jsonSize', language)} value={fmtBytes(metrics.jsonBytes)} />
      </div>
      <div className="h-px bg-border my-3.5" />
      <div className="text-xs">
        Status: <span className="font-mono">{status}</span>{' · '}
        <span className={`font-black ${qParse.cls}`}>Parse: {qParse.label}</span>
      </div>
      <div className="h-px bg-border my-3.5" />
      {preview.length > 0 ? (
        <div className="border border-border rounded-2xl overflow-hidden bg-black/[.16]">
          <table className="w-full border-collapse">
            <thead><tr>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">id</th>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">sku</th>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">region</th>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">qty</th>
              <th className="text-xs text-muted font-mono bg-white/[.02] p-3 text-left">price</th>
            </tr></thead>
            <tbody>
              {preview.slice(0, 50).map((row) => (
                <tr key={row.id} className="border-b border-white/5">
                  <td className="text-sm p-3">{row.id}</td>
                  <td className="text-sm p-3">{row.sku}</td>
                  <td className="text-sm p-3">{row.region}</td>
                  <td className="text-sm p-3">{row.qty}</td>
                  <td className="text-sm p-3">{row.price.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-muted">{autoRun ? t('status.autoRun', language) : t('status.pressStart', language)}</div>
      )}
    </TestCard>
  );
}
